/**
 * 3-card KPI section (Quality / Delivery / Financial) for Cached Performance
 * tab (CR-4751). Mirrors KPIMetricsSection visual language from DefaultTab.
 */
import type {
  DashboardSnapshot, MetricType, PeriodType, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import type { DeliveryMetricMode } from '../../../lib/api'
import { CachedKPICell } from './CachedKPICell'
import { DAILY_HIDDEN_NOTE, LOWER_IS_BETTER, asNumber } from './helpers'
import { showsOtd, showsOtif } from '../deliveryMetricMode'
import { getQualityColorFromBands, type QualityGradeBands } from '../SiteKPIComponents'

/** Tailwind needs whole class names: Delivery card columns by tile count. */
const DELIVERY_COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }

interface CachedKPISectionProps {
  latestByMetric: Map<MetricType, DashboardSnapshot>
  trends: Record<MetricType, TrendPoint[]>
  period?: PeriodType
  /** Period metrics with nothing to measure in the latest capture (CR-4751). */
  naMetrics?: Set<MetricType>
  /** CR-8697: the customer's Delivery Metric Mode — hides OTIF / OTD tiles it switches off. */
  deliveryMetricMode?: DeliveryMetricMode
  /** CR-4751: the customer's PPM colour bands (the live card's), for the PPM badge. */
  ppmGradeBands?: QualityGradeBands
}

export function CachedKPISection({
  latestByMetric, trends, period = 'monthly', naMetrics = new Set<MetricType>(), deliveryMetricMode,
  ppmGradeBands = [],
}: CachedKPISectionProps) {
  const cell = (metric: MetricType) => ({
    snapshot: latestByMetric.get(metric),
    trend: trends[metric],
    notApplicable: naMetrics.has(metric),
    lowerIsBetter: LOWER_IS_BETTER.includes(metric),
  })
  // CR-4751: coloured by the customer's PPM bands, as the live card is — the
  // badge was always red, 0 ppm included.
  const ppm = latestByMetric.get('ppm')
  const ppmBadge = ppm ? getQualityColorFromBands(asNumber(ppm.metric_value), ppmGradeBands, 0) : undefined
  const daily = period === 'daily'
  const showOTIF = showsOtif(deliveryMetricMode)
  const showOTD = showsOtd(deliveryMetricMode)
  // The Daily note stands in for OTIF / OTD, so it goes when both are off.
  const showDailyNote = daily && (showOTIF || showOTD)
  const deliveryTiles = 1 + (showDailyNote ? 1 : 0) + (!daily && showOTIF ? 1 : 0) + (!daily && showOTD ? 1 : 0)

  return (
    <div className="bg-card rounded-lg shadow-sm p-4">
      {/* CR-4751: the three cards stack below xl — side by side, each money
          figure had ~45px at a 1,030px window and the amounts ran together. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
        {/* Quality */}
        <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-red-600 mb-4 text-sm uppercase tracking-wide">Quality</h3>
          <div className="grid grid-cols-2 gap-3 items-start flex-1">
            <CachedKPICell label="PPM" {...cell('ppm')} badgeColor={ppmBadge} />
            <CachedKPICell label="NCRs" {...cell('ncr_count')} />
          </div>
        </div>

        {/* Delivery — CR-4751: a single day does not measure OTIF or OTD.
            CR-8697: each shows only while the Delivery Metric Mode keeps it. */}
        <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-green-600 mb-4 text-sm uppercase tracking-wide">Delivery</h3>
          <div className={`grid ${DELIVERY_COLS[deliveryTiles]} gap-3 items-start flex-1`}>
            {showDailyNote && (
              <p className="text-xs text-muted-foreground text-center self-center">{DAILY_HIDDEN_NOTE}</p>
            )}
            {!daily && showOTIF && <CachedKPICell label="OTIF" {...cell('otif_percentage')} />}
            {!daily && showOTD && <CachedKPICell label="OTD" {...cell('otd_percentage')} />}
            <CachedKPICell label="Appeals" {...cell('appeals_count')} />
          </div>
        </div>

        {/* Financial */}
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-blue-600 mb-4 text-sm uppercase tracking-wide">Financial</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <CachedKPICell label="Open Orders" {...cell('open_order_value')} />
            <CachedKPICell label="Total Spend" {...cell('total_spend')} />
            <CachedKPICell label="Arrears" {...cell('arrears_value')} />
          </div>
        </div>
      </div>
    </div>
  )
}
